/* Redesigned PDP behaviour. Commerce logic stays in Dawn (product-info, product-form, cart-drawer);
   this file only adds UI on top of it. */
(() => {
  const onReady = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());

  const currencyFrom = (text) => (text || '').replace(/[\d.,\s]/g, '').trim() || '£';
  const formatMoney = (cents, sample) => {
    const symbol = currencyFrom(sample);
    const value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return symbol + value;
  };

  const sectionRoots = () => document.querySelectorAll('product-info.rs-product');
  const mainButton = (root) => root.querySelector('button[id^="ProductSubmitButton-"]');
  const currentPriceText = (root) =>
    root.querySelector('[id^="price-"] .price__sale .price-item--last, [id^="price-"] .price:not(.price--on-sale) .price__regular .price-item--regular')
      ?.textContent.trim();

  const selectedVariant = (root) => JSON.parse(root.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
  const quantityOf = (root) => Math.max(1, parseInt(root.querySelector('.quantity__input')?.value, 10) || 1);
  /* Unit price in pence: from the selected variant, else parsed from the price shown on the page */
  const unitCents = (root, variant) => {
    const v = variant || selectedVariant(root);
    if (v && typeof v.price === 'number') return v.price;
    const n = parseFloat((currentPriceText(root) || '').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  /* Price for the chosen quantity, e.g. 3 x £599.00 = £1,797.00 */
  const lineTotalText = (root, variant) => {
    const sample = currentPriceText(root);
    const unit = unitCents(root, variant);
    return unit === null ? sample : formatMoney(unit * quantityOf(root), sample);
  };

  /* Price inside the Add to basket button + instalment amount */
  function syncPrices(root, variant) {
    const price = currentPriceText(root);
    const total = lineTotalText(root, variant);
    const btn = mainButton(root);
    if (btn) {
      let slot = btn.querySelector('.rs-atc-price');
      if (!slot) {
        const sep = document.createElement('span');
        sep.className = 'rs-sep';
        sep.setAttribute('aria-hidden', 'true');
        slot = document.createElement('span');
        slot.className = 'rs-atc-price';
        const spinner = btn.querySelector('.loading__spinner');
        btn.insertBefore(sep, spinner);
        btn.insertBefore(slot, spinner);
      }
      if (total) slot.textContent = total;
      btn.classList.toggle('rs-no-price', btn.disabled);
    }
    const bnpl = root.querySelector('[data-rs-bnpl]');
    if (bnpl && variant && typeof variant.price === 'number') {
      const n = Number(bnpl.dataset.instalments || 3);
      const amount = bnpl.querySelector('[data-rs-bnpl-amount]');
      if (amount) amount.textContent = formatMoney(Math.round(variant.price / n), price);
    }
  }

  /* Gallery: arrows + "1 / 15" counter on desktop, driven by Dawn's thumbnails */
  function enhanceGallery(root) {
    const gallery = root.querySelector('media-gallery');
    const viewer = gallery?.querySelector('[id^="GalleryViewer-"]');
    const thumbs = gallery ? [...gallery.querySelectorAll('.thumbnail-list__item button.thumbnail')] : [];
    if (!viewer || thumbs.length < 2 || viewer.querySelector('.rs-stage-ui')) return;
    const ui = document.createElement('div');
    ui.className = 'rs-stage-ui';
    ui.innerHTML =
      '<button type="button" class="rs-nav-btn rs-prev" aria-label="Previous image"><svg class="rs-i"><use href="#rs-i-left"/></svg></button>' +
      '<button type="button" class="rs-nav-btn rs-next" aria-label="Next image"><svg class="rs-i"><use href="#rs-i-right"/></svg></button>' +
      '<span class="rs-count" aria-live="polite"></span>';
    viewer.appendChild(ui);
    const current = () => Math.max(0, thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true'));
    const update = () => (ui.querySelector('.rs-count').textContent = `${current() + 1} / ${thumbs.length}`);
    const go = (dir) => {
      const next = (current() + dir + thumbs.length) % thumbs.length;
      thumbs[next].click();
      thumbs[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      requestAnimationFrame(update);
    };
    ui.querySelector('.rs-prev').addEventListener('click', () => go(-1));
    ui.querySelector('.rs-next').addEventListener('click', () => go(1));
    thumbs.forEach((t) => t.addEventListener('click', () => requestAnimationFrame(update)));
    update();
  }

  /* Pairs well with: option toggle + add to basket through Dawn's cart drawer */
  async function addToCart(variantId, button) {
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer?.getSectionsToRender?.().map((s) => s.id) || [];
    button.disabled = true;
    try {
      const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add');
      button.classList.add('rs-on');
      button.querySelector('.rs-add-lbl').textContent = 'Added';
      if (drawer?.renderContents) {
        drawer.classList.remove('is-empty');
        drawer.renderContents({ ...data, id: Number(variantId) });
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (e) {
      button.querySelector('.rs-add-lbl').textContent = 'Try again';
      console.error(e);
    } finally {
      button.disabled = false;
    }
  }

  document.addEventListener('click', (event) => {
    const segBtn = event.target.closest('[data-rs-pair] .rs-seg button');
    if (segBtn) {
      const card = segBtn.closest('[data-rs-pair]');
      card.querySelectorAll('.rs-seg button').forEach((b) => {
        const on = b === segBtn;
        b.classList.toggle('rs-on', on);
        b.setAttribute('aria-checked', String(on));
      });
      const add = card.querySelector('[data-rs-pair-add]');
      add.dataset.variantId = segBtn.dataset.variantId;
      add.classList.remove('rs-on');
      add.querySelector('.rs-add-lbl').textContent = `Add - ${segBtn.dataset.price}`;
      const price = card.querySelector('[data-rs-pair-price]');
      if (price) price.textContent = segBtn.dataset.price;
      return;
    }
    const addBtn = event.target.closest('[data-rs-pair-add]');
    if (addBtn) addToCart(addBtn.dataset.variantId, addBtn);
  });

  /* Sticky add to basket (mobile) */
  if (!customElements.get('rs-sticky-atc')) {
    customElements.define(
      'rs-sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          this.root = this.closest('product-info') || document.querySelector('product-info.rs-product');
          this.button = this.querySelector('[data-rs-sticky-button]');
          this.button.addEventListener('click', () => {
            const main = mainButton(this.root);
            if (main && !main.disabled) main.click();
          });
          const target = mainButton(this.root);
          if (!target || !('IntersectionObserver' in window)) return;
          new IntersectionObserver(([entry]) => {
            const show = !entry.isIntersecting;
            this.classList.toggle('rs-show', show);
            this.setAttribute('aria-hidden', String(!show));
            this.button.tabIndex = show ? 0 : -1;
            document.body.classList.toggle('rs-sticky-on', show);
          }).observe(target);
        }
        sync(variant) {
          const main = mainButton(this.root);
          const total = lineTotalText(this.root, variant);
          if (total) this.querySelector('[data-rs-sticky-price]').textContent = total;
          if (variant?.title && variant.title !== 'Default Title') this.querySelector('[data-rs-sticky-variant]').textContent = variant.title;
          if (main) {
            this.button.disabled = main.disabled;
            this.button.textContent = main.disabled ? main.querySelector('span')?.textContent.trim() : 'Add to basket';
          }
        }
      }
    );
  }

  /* "Pairs well with": load straight away instead of waiting until it scrolls into view,
     so it's never caught empty on slow connections or quick scrolls */
  const loadPairsNow = () => {
    document.querySelectorAll('product-recommendations.rs-pairs-bb').forEach((el) => {
      if (el.dataset.rsLoaded || typeof el.loadRecommendations !== 'function') return;
      el.dataset.rsLoaded = 'true';
      el.observer?.disconnect();
      el.loadRecommendations(el.dataset.productId);
    });
  };

  onReady(() => {
    loadPairsNow();
    sectionRoots().forEach((root) => {
      syncPrices(root, selectedVariant(root));
      enhanceGallery(root);
    });
    /* Quantity +/- or typing updates the button and sticky bar totals */
    const onQuantity = (event) => {
      const root = event.target.closest?.('product-info.rs-product');
      if (!root || !event.target.matches('.quantity__input')) return;
      syncPrices(root, selectedVariant(root));
      root.querySelector('rs-sticky-atc')?.sync(selectedVariant(root));
    };
    document.addEventListener('change', onQuantity);
    document.addEventListener('input', onQuantity);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        requestAnimationFrame(() => {
          sectionRoots().forEach((root) => {
            syncPrices(root, event.data?.variant);
            enhanceGallery(root);
            root.querySelector('rs-sticky-atc')?.sync(event.data?.variant);
          });
        });
      });
    }
  });
})();
